import React, { useState, useRef } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { categories } from '../data/categories';
import { UIStrings } from '../../../data/strings';
import { FormItem, I18nString, LangId } from '../../../shared/types';
import FormViewer from './FormViewer';
import ImageLightbox from '../../../components/ImageLightbox';
import OfficeMap from '../../office-map/components/OfficeMap';

interface Props {
  form: FormItem;
  t: (obj: I18nString) => string;
  lang: LangId;
}

// ── 가로 스와이프 이미지 캐러셀 ────────────────────────────────
const ImageCarousel: React.FC<{ images: string[] }> = ({ images }) => {
  const [current, setCurrent] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const startX = useRef(0);
  const isDragging = useRef(false);

  const goTo = (idx: number) => setCurrent(Math.max(0, Math.min(images.length - 1, idx)));

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const diff = startX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) goTo(current + (diff > 0 ? 1 : -1));
    else setLightboxIdx(current); // 스와이프 없이 탭만 했으면 라이트박스 열기
    isDragging.current = false;
  };

  return (
    <div className="select-none">
      {/* 이미지 슬라이드 */}
      <div
        className="overflow-hidden rounded-2xl cursor-zoom-in"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={() => setLightboxIdx(current)}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {images.map((src, idx) => (
            <img
              key={idx}
              src={src}
              alt={`guide ${idx + 1}`}
              className="w-full flex-shrink-0 object-contain bg-white"
              draggable={false}
            />
          ))}
        </div>
      </div>

      {/* 페이지 인디케이터 + 카운터 */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className={`transition-all duration-200 rounded-full
                ${current === idx
                  ? 'w-5 h-2 bg-gusring-text'
                  : 'w-2 h-2 bg-gusring-border-strong hover:bg-gusring-yellow'
                }`}
            />
          ))}
          <span className="ml-1 text-[11px] font-bold text-gusring-text-hint">
            {current + 1} / {images.length}
          </span>
        </div>
      )}

      {/* 라이트박스 */}
      {lightboxIdx !== null && (
        <ImageLightbox
          images={images}
          initialIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
};

// ── 아코디언 섹션 ──────────────────────────────────────────────
const AccordionSection: React.FC<{
  index: number;
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ index, label, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-3xl border border-gusring-border bg-gusring-surface shadow-card overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left btn-press"
      >
        <div className="w-8 h-8 bg-gusring-yellow text-gusring-brand-950 rounded-full flex items-center justify-center text-sm font-black shrink-0">
          {index}
        </div>
        <span className="flex-1 font-black text-[15px] text-gusring-text">{label}</span>
        <ChevronDown
          size={18}
          className={`text-gusring-text-hint transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className={`transition-all duration-300 ease-out overflow-hidden ${open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-4 pb-5">
          {children}
        </div>
      </div>
    </div>
  );
};

// ── 메인 DetailView ────────────────────────────────────────────
const DetailView: React.FC<Props> = ({ form, t, lang }) => {
  const guideImages = form.guideImages?.[lang] ?? [];

  return (
    <div className="animate-slide-up pb-32">

      {/* 서류명 + 카테고리 */}
      <div className="px-5 pt-5 pb-5">
        <span className="inline-flex items-center bg-gusring-yellow-soft text-gusring-brand-700 text-[10px] font-black px-2.5 py-1 rounded-full mb-2.5 uppercase tracking-tight">
          {t(categories[form.cat])}
        </span>
        <h2 className="text-xl sm:text-2xl font-black text-gusring-text tracking-tight leading-tight mb-1.5">
          {t(form.title)}
        </h2>
        <p className="text-[13px] text-gusring-text-sub leading-relaxed">
          {t(form.desc)}
        </p>
      </div>

      {/* 아코디언 섹션들 */}
      <div className="px-4 space-y-3">

        {/* 1. 양식 보기 */}
        <AccordionSection index={1} label={t(UIStrings.viewForm)}>
          <FormViewer form={form} t={t} lang={lang} />
        </AccordionSection>

        {/* 2. 안내사항 보기 */}
        <AccordionSection index={2} label={t(UIStrings.viewGuide)}>
          {guideImages.length > 0 ? (
            <ImageCarousel images={guideImages} />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-10 px-6 text-center bg-gusring-bg rounded-2xl">
              <img src="/sign.png" alt="preparing" className="w-28 h-auto object-contain" />
              <div className="space-y-1">
                <p className="font-black text-gusring-text text-[15px]">
                  {t({ ko: '이미지 준비중', en: 'Image coming soon', zh: '图片准备中', ja: '画像準備中', vi: 'Đang chuẩn bị ảnh' })}
                </p>
                <p className="text-[12px] text-gusring-text-hint leading-relaxed">
                  {t({ ko: '안내 이미지를 준비하고 있어요.', en: "We're preparing the guide images.", zh: '我们正在准备说明图片。', ja: '案内画像を準備しています。', vi: 'Chúng tôi đang chuẩn bị hình ảnh hướng dẫn.' })}
                </p>
              </div>
            </div>
          )}
        </AccordionSection>

        {/* 3. 행정 정보 (수수료, 준비물 등) */}
        {(form.fee || form.requirements || form.notes) && (
          <AccordionSection index={3} label={t({ ko: '행정 정보 안내', en: 'Administrative Info', zh: '行政信息', ja: '行政情報', vi: 'Thông tin hành chính' })}>

            {/* 창구 안내 지도 */}
            <OfficeMap lang={lang} />

            <div className="space-y-4 py-2 mt-4">
              {form.fee && (
                <div>
                  <h4 className="text-[13px] font-bold text-gusring-text mb-1 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gusring-yellow rounded-full"></span>
                    {t({ ko: '수수료', en: 'Fee', zh: '费用', ja: '手数料', vi: 'Lệ phí' })}
                  </h4>
                  <p className="text-[13px] text-gusring-text-sub ml-3.5">{t(form.fee as I18nString)}</p>
                </div>
              )}
              {form.requirements && (
                <div>
                  <h4 className="text-[13px] font-bold text-gusring-text mb-1 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gusring-yellow rounded-full"></span>
                    {t({ ko: '준비물', en: 'Requirements', zh: '准备물', ja: '準備物', vi: 'Yêu cầu' })}
                  </h4>
                  <p className="text-[13px] text-gusring-text-sub ml-3.5">{t(form.requirements as I18nString)}</p>
                </div>
              )}
              {form.notes && (
                <div className="bg-gusring-bg/50 p-3 rounded-2xl border border-gusring-border">
                  <h4 className="text-[12px] font-bold text-gusring-text-sub mb-1 flex items-center gap-1.5">
                    💡 {t({ ko: '참고사항', en: 'Notes', zh: '注意事项', ja: '注意事項', vi: 'Lưu ý' })}
                  </h4>
                  <p className="text-[12px] text-gusring-text-hint leading-relaxed">{t(form.notes as I18nString)}</p>
                </div>
              )}
            </div>
          </AccordionSection>
        )}

      </div>
    </div>
  );
};

// ── 다운로드 FAB (단일 버튼) ──────────────────────────────────
export const DetailViewFAB: React.FC<{
  t: (obj: I18nString) => string;
  isDownloading: boolean;
  hasImage: boolean;
  onDownload: () => void;
}> = ({ t, isDownloading, hasImage, onDownload }) => {
  if (!hasImage) return null;

  const fabPos: React.CSSProperties = {
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
    right:  'max(1.25rem, calc((100vw - 448px) / 2 + 1.25rem))',
  };

  return (
    <button
      className="fixed z-40 flex items-center gap-3 pl-4 pr-3 py-3 btn-primary rounded-2xl shadow-yellow
                 active:scale-95 active:brightness-105
                 transition-[transform,filter] duration-150 disabled:opacity-60
                 animate-scale-in"
      style={fabPos}
      onClick={onDownload}
      disabled={isDownloading}
    >
      <span className="text-gusring-brand-950 text-[13px] font-black tracking-tight whitespace-nowrap select-none">
        {isDownloading ? t(UIStrings.downloading) : t(UIStrings.download)}
      </span>
      <div className="w-8 h-8 bg-gusring-brand-950/10 rounded-xl flex items-center justify-center">
        {isDownloading
          ? <span className="text-sm animate-spin inline-block">⏳</span>
          : <Download size={16} className="text-gusring-brand-950" />
        }
      </div>
    </button>
  );
};

export default DetailView;
